import axios from "axios";
import moment from "moment";
import { toast } from "react-toastify";
import { convertTimeStamp } from "./timeValidation.js";

/**
 * API Helper Functions
 * Functions for making API calls related to timer operations
 */

/**
 * Duplicate a timer entry
 * @param {Object} params - Parameters object
 * @param {string} params.task - Task name
 * @param {string} params.project_id - Project ID
 * @param {string} params.tag_id - Tag ID
 * @param {string} params.client - Client name
 * @param {string} params.clientId - Client ID
 * @param {string} params.date - Date string
 * @param {string} params.startTime - Start time
 * @param {string} params.endTime - End time
 * @param {number} params.duration - Duration in seconds
 * @param {string|null} params.sprint_name - Sprint name
 * @param {string|null} params.sprint_id - Sprint ID
 * @param {string} params._id - Entry ID
 * @param {boolean} params.billable - Billable status
 * @param {string} params.selectedUser - Selected user ID
 * @param {string} params.token - Auth token
 * @param {Function} params.dispatch - Redux dispatch function
 * @param {Function} params.setTimerData - Redux action creator
 * @param {Function} params.setDuplicateLoading - Setter for duplicate loading state
 * @param {Function} params.setActiveDropdownId - Setter for active dropdown ID
 * @returns {Promise} API response
 */
export const duplicateTimerEntry = async ({
  task,
  project_id,
  tag_id,
  client,
  clientId,
  date,
  startTime,
  endTime,
  duration,
  sprint_name,
  sprint_id,
  _id,
  billable,
  selectedUser,
  token,
  dispatch,
  setTimerData,
  setDuplicateLoading,
  setActiveDropdownId,
}) => {
  const postData = {
    task: task,
    project: project_id,
    date: endTime,
    tag: tag_id,
    billable: billable,
    startTime: startTime,
    endTime: endTime,
    duration: duration,
    client: client,
    clientId: clientId,
    user: selectedUser,
    id: _id,
    tz: global.config.getTimeZone(),
  };
  if (sprint_id) {
    postData.sprint = sprint_id;
  }
  setDuplicateLoading(true);
  try {
    const response = await axios.post("/api/time-track/importdata/duplicate", postData, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    setDuplicateLoading(false);
    await dispatch(setTimerData(Date.now()));

    global.config.activityLog(
      window.location.href,
      "Timer",
      `Duplicate created Successfully`
    );
    toast("Duplicate created Successfully", {
      position: "bottom-right",
      autoClose: 2000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
    });
    await dispatch(setTimerData(Date.now()));
    setActiveDropdownId(null);
    return response;
  } catch (error) {
    setDuplicateLoading(false);
    throw error;
  }
};

/**
 * Update timer entry
 * @param {Object} params - Parameters object
 * @param {string} params.id - Entry ID
 * @param {string} params.task - Task name
 * @param {Date} params.dateEdit - Date object
 * @param {string} params.startTimeEdit - Start time string
 * @param {string} params.endTimeEdit - End time string
 * @param {string} params.clientEdit - Client name
 * @param {string} params.clientIdEdit - Client ID
 * @param {string} params.projectIdEdit - Project ID
 * @param {string} params.sprintIdEdit - Sprint ID
 * @param {string} params.tagIdEdit - Tag ID
 * @param {boolean} params.billableEdit - Billable status
 * @param {number} params.uDuration - Duration in seconds
 * @param {Date} params.dateOldEdit - Old date object
 * @param {string} params.selectedUser - Selected user ID
 * @param {string} params.token - Auth token
 * @param {Function} params.dispatch - Redux dispatch function
 * @param {Function} params.setTimerData - Redux action creator
 * @param {Function} params.setIsUpdateLoading - Setter for update loading state
 * @param {Function} params.setExpandedGroups - Setter for expanded groups
 * @param {Function} params.refetchTimerData - Function to refetch timer data
 * @param {Function} params.resetEditState - Function to reset edit state
 * @param {Object} params.resetSetters - Object containing reset setters
 * @returns {Promise} API response
 */
export const updateTimerEntry = async ({
  id,
  task,
  dateEdit,
  startTimeEdit,
  endTimeEdit,
  clientEdit,
  clientIdEdit,
  projectIdEdit,
  sprintIdEdit,
  tagIdEdit,
  billableEdit,
  uDuration,
  dateOldEdit,
  selectedUser,
  token,
  dispatch,
  setTimerData,
  setIsUpdateLoading,
  setExpandedGroups,
  refetchTimerData,
  resetEditState,
  resetSetters,
}) => {
  const startTimeFormated = convertTimeStamp(dateEdit, startTimeEdit && startTimeEdit.trim());
  const endTimeFormated = convertTimeStamp(dateEdit, endTimeEdit && endTimeEdit.trim());
  const startdate = moment
    .tz(dateEdit, global.config.getTimeZone())
    .utc()
    .format("YYYY-MM-DD HH:mm:ss");

  const postData = {
    task: task,
    project: projectIdEdit,
    date: startdate,
    oldDate: dateOldEdit,
    tag: tagIdEdit,
    billable: billableEdit,
    startTime: startTimeFormated,
    endTime: endTimeFormated,
    duration: uDuration,
    client: clientEdit,
    clientId: clientIdEdit,
    user: selectedUser,
    id: id,
    tz: global.config.getTimeZone(),
  };

  if (sprintIdEdit) {
    postData.sprint = sprintIdEdit;
  } else {
    postData.sprint = null;
  }

  setIsUpdateLoading(true);
  try {
    const response = await axios.post("/api/time-track/importdata/custome-time-update", postData, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    await dispatch(setTimerData(Date.now()));
    dispatch(setTimerData(Date.now()));
    setExpandedGroups([]);
    global.config.activityLog(
      window.location.href,
      "Timer",
      `Time Updated Successfully`
    );
    toast("Time Updated Successfully", {
      position: "bottom-right",
      autoClose: 2000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
    });
    setIsUpdateLoading(false);
    resetEditState(resetSetters);
    refetchTimerData();
    return response;
  } catch (error) {
    setIsUpdateLoading(false);
    throw error;
  }
};

/**
 * Delete timer entry
 * @param {Object} params - Parameters object
 * @param {string} params.taskId - Task ID
 * @param {string} params.userId - User ID
 * @param {string} params.token - Auth token
 * @param {Function} params.setIsDeleteLoading - Setter for delete loading state
 * @param {Function} params.setDeleteId - Setter for delete ID
 * @param {Function} params.refetchTimerData - Function to refetch timer data
 * @param {Function} params.dispatch - Redux dispatch function
 * @param {Function} params.setTimerData - Redux action creator
 * @param {Object} params.group - Group object
 * @param {Function} params.setExpandedGroups - Setter for expanded groups
 * @param {Function} params.handleDropdownToggle - Function to toggle dropdown
 * @returns {Promise} API response
 */
export const deleteTimerEntry = async ({
  taskId,
  userId,
  token,
  setIsDeleteLoading,
  setDeleteId,
  refetchTimerData,
  dispatch,
  setTimerData,
  group,
  setExpandedGroups,
  handleDropdownToggle,
}) => {
  setIsDeleteLoading(true);
  setDeleteId(taskId);
  try {
    const response = await axios.delete(`/api/time-track/importdata/delete-timer/${taskId}/${userId}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    toast("Deleted Successfully", {
      position: "bottom-right",
      autoClose: 2000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
    });
    setIsDeleteLoading(false);
    refetchTimerData();
    await dispatch(setTimerData(Date.now()));
    // Check if group entries length is 2 and reset expandedGroups
    if (group.entries.length === 2) {
      setExpandedGroups([]);
    }
    handleDropdownToggle();
    return response;
  } catch (error) {
    console.log(error);
    global.config.slackMessage(error.toString());
    setIsDeleteLoading(false);
    throw error;
  }
};
